import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { billingService } from "@/services/billingService";
import { toast } from "sonner";

interface Plan {
  id: string;
  name: string;
  description: string;
  priceId: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  features: string[];
  recommended?: boolean;
}

interface PlanSelectionStepProps {
  onPlanSelected?: (planId: string) => void;
  onComplete?: () => void;
  onBack?: () => void;
}

const PlanSelectionStep = ({ onPlanSelected, onComplete, onBack }: PlanSelectionStepProps) => {
  const { user } = useUser();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  // Fetch plans from backend (with Stripe prices)
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const backendPlans = await billingService.getPlans();
        if (backendPlans && Array.isArray(backendPlans)) {
          // Filter out free plan and mark solo as recommended
          const paidPlans = backendPlans
            .filter((p: Plan) => p.id !== 'free')
            .map((p: Plan) => ({
              ...p,
              recommended: p.id === 'solo'
            }));
          setPlans(paidPlans);
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error);
        // Plans will remain empty, component will show loading/error state
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);
  
  // Check if returning from payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentFailed = urlParams.get('paymentFailed') === 'true';
    const planCompleted = sessionStorage.getItem('onboarding-plan-completed') === 'true';
    const planSelected = sessionStorage.getItem('onboarding-plan-selected');
    
    if (paymentFailed) {
      // Payment failed, show error message
      toast.error("Payment was cancelled or failed. Please try again or use a promotion code if you have one.");
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    if (planCompleted && planSelected) {
      // Clean up session storage
      sessionStorage.removeItem('onboarding-plan-completed');
      sessionStorage.removeItem('onboarding-plan-selected');
      sessionStorage.removeItem('onboarding-in-progress');
      
      // Notify parent that plan was selected
      onPlanSelected?.(planSelected);
      
      // Move to next step
      onComplete?.();
    }
  }, [onPlanSelected, onComplete]);

  const handlePlanSelect = async (plan: Plan) => {
    setSelectedPlan(plan.id);
    setIsProcessing(true);
    
    try {
      console.log("Creating Stripe checkout session for plan:", plan.id);
      
      if (!plan.priceId) {
        throw new Error(`No price ID found for plan: ${plan.id}`);
      }
      
      console.log("Using price ID:", plan.priceId);
      console.log("Creating checkout session for user:", { id: user!.id, email: user!.email });
      
      // Use the billing service with priceId from backend
      const data = await billingService.createCheckoutSession({
        priceId: plan.priceId,
      });
      
      const { url } = data;
      
      if (!url) {
        throw new Error("No checkout URL returned");
      }
      
      // Save onboarding state before redirect
      sessionStorage.setItem('onboarding-in-progress', 'true');
      sessionStorage.setItem('onboarding-plan-selected', plan.id);
      
      // Redirect to checkout
      window.location.href = url;
      
    } catch (error: any) {
      console.error("Error processing plan selection:", error);
      toast.error(error.message || "Failed to process plan selection");
      setSelectedPlan(null);
      setIsProcessing(false);
    }
  };

  // Format price from cents to display format
  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  };



  if (isLoadingPlans) {
    return (
      <div className="w-full max-w-5xl mx-auto px-2 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading plans...</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-2">
      {/* Plan selection header */}
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-bold mb-2">Choose Your Plan</h2>
        <p className="text-sm md:text-base text-muted-foreground mb-4 px-2">
          Select the plan that fits your needs. Have a promo code? Enter it during checkout.
        </p>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {plans.map((plan) => (
          <Card 
            key={plan.id} 
            className={`relative ${plan.recommended ? 'border-primary shadow-lg md:scale-105' : ''}`}
          >
            {plan.recommended && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="px-3 py-1">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Recommended
                </Badge>
              </div>
            )}
            
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">{plan.name}</CardTitle>
              <CardDescription className="text-2xl font-bold mt-1">
                {formatPrice(plan.amount, plan.currency)}
                <span className="text-sm font-normal text-muted-foreground">
                  /{plan.interval}
                </span>
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-3">
              <ul className="space-y-2">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
            </CardContent>
            
            <CardFooter>
              <Button 
                className="w-full h-11 text-base" 
                variant={plan.recommended ? 'default' : 'outline'}
                onClick={() => handlePlanSelect(plan)}
                disabled={isProcessing}
              >
                {isProcessing && selectedPlan === plan.id ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Choose ${plan.name}`
                )}
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      <div className="text-center mt-6 space-y-2">
        <p className="text-sm text-muted-foreground">
          Start your subscription today. Cancel anytime.
        </p>
        <p className="text-xs text-muted-foreground">
          Have a promo code? Enter it during checkout for discounts or free access.
        </p>
        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
          <span>✓ Cancel anytime</span>
          <span>✓ No hidden fees</span>
          <span>✓ Secure payment</span>
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionStep;